import { useState, useEffect } from 'react';
import AdminNav from '@/components/AdminNav';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Download, Save, AlertCircle, Building2, Sparkles, Wand2, Check, X, Eye } from 'lucide-react';

const OUTCOMES = [
  { value: 'death', label: 'Death' },
  { value: 'days_away', label: 'Days away from work' },
  { value: 'job_transfer', label: 'Job transfer / restriction' },
  { value: 'other_recordable', label: 'Other recordable' },
];

const ILLNESS_TYPES = [
  { value: 'injury', label: 'Injury' },
  { value: 'skin_disorder', label: 'Skin Disorder' },
  { value: 'respiratory', label: 'Respiratory' },
  { value: 'poisoning', label: 'Poisoning' },
  { value: 'hearing_loss', label: 'Hearing Loss' },
  { value: 'other', label: 'Other Illness' },
];

export default function AdminOSHAReports({ user, onLogout }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [incidents, setIncidents] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingCompany, setSavingCompany] = useState(false);
  const [aiClassifyingId, setAiClassifyingId] = useState(null);
  const [aiBatchRunning, setAiBatchRunning] = useState(false);

  useEffect(() => {
    load();
  }, [year]);

  const load = async () => {
    setLoading(true);
    try {
      const [incRes, coRes] = await Promise.all([
        axiosInstance.get(`/osha-reports/incidents?year=${year}`),
        axiosInstance.get('/osha-reports/company'),
      ]);
      setIncidents(incRes.data || []);
      setCompanyInfo(coRes.data || {});
    } catch (e) {
      console.error(e);
      toast.error('Failed to load OSHA data');
    } finally {
      setLoading(false);
    }
  };

  const patchIncident = async (id, oshaPatch) => {
    setSavingId(id);
    try {
      await axiosInstance.patch(`/osha-reports/incidents/${id}/osha`, oshaPatch);
      setIncidents((prev) => prev.map((i) => (i.id === id ? { ...i, osha: { ...i.osha, ...oshaPatch } } : i)));
    } catch (e) {
      console.error(e);
      toast.error('Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await axiosInstance.patch('/osha-reports/company', companyInfo);
      toast.success('Company OSHA info saved');
    } catch (e) {
      console.error(e);
      toast.error('Save failed');
    } finally {
      setSavingCompany(false);
    }
  };

  const downloadPdf = async (path, filename) => {
    try {
      const res = await axiosInstance.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error('Download failed');
    }
  };

  const previewPdf = async (path) => {
    try {
      const res = await axiosInstance.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      // Revoke after 60s to release memory but give the tab time to load
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error(e);
      toast.error('Preview failed');
    }
  };

  const aiClassify = async (id) => {
    setAiClassifyingId(id);
    try {
      const res = await axiosInstance.post(`/osha-reports/incidents/${id}/ai-classify`);
      const suggestion = res.data;
      setIncidents((prev) => prev.map((i) =>
        i.id === id ? { ...i, osha: { ...i.osha, aiSuggestion: suggestion } } : i
      ));
      toast.success(`AI: ${suggestion.recordable ? 'Recordable' : 'Not recordable'} (${suggestion.confidence})`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'AI classification failed');
    } finally {
      setAiClassifyingId(null);
    }
  };

  const aiClassifyAll = async () => {
    setAiBatchRunning(true);
    try {
      const res = await axiosInstance.post(`/osha-reports/incidents/ai-classify-all?year=${year}`);
      toast.success(`AI classified ${res.data.processed} new · ${res.data.skipped} already done`);
      await load();
    } catch (e) {
      console.error(e);
      toast.error('Batch AI classification failed');
    } finally {
      setAiBatchRunning(false);
    }
  };

  const acceptAiSuggestion = async (inc) => {
    const s = inc.osha?.aiSuggestion;
    if (!s) return;
    await patchIncident(inc.id, {
      recordable: !!s.recordable,
      outcome: s.outcome || undefined,
      injuryOrIllnessType: s.injuryOrIllnessType || 'injury',
    });
    toast.success('Applied AI suggestion');
  };

  const recordableCount = incidents.filter((i) => i.osha?.recordable).length;
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav user={user} onLogout={onLogout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => window.history.back()} className="mb-4" data-testid="osha-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              OSHA Recordkeeping Reports
            </h1>
            <p className="text-gray-600 mt-2 max-w-2xl">
              Auto-generate OSHA Form 300 (annual log), 300A (posted summary), and 301 (per-incident reports) from your recorded incidents.
            </p>
          </div>
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-xs text-gray-500">Reporting year</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-32" data-testid="osha-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Download buttons */}
        <Card className="mb-6" data-testid="osha-downloads-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-600" /> Downloads
            </CardTitle>
            <CardDescription>
              {recordableCount} recordable case{recordableCount !== 1 ? 's' : ''} identified for {year}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button
              onClick={() => previewPdf(`/osha-reports/300/pdf?year=${year}`)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="preview-300-btn"
            >
              <Eye className="w-4 h-4 mr-2" /> Preview 300 Log
            </Button>
            <Button
              onClick={() => downloadPdf(`/osha-reports/300/pdf?year=${year}`, `OSHA_300_Log_${year}.pdf`)}
              variant="outline"
              data-testid="download-300-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Download 300 Log
            </Button>
            <Button
              onClick={() => downloadPdf(`/osha-reports/300a/pdf?year=${year}`, `OSHA_300A_Summary_${year}.pdf`)}
              variant="outline"
              data-testid="download-300a-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Form 300A (Summary)
            </Button>
            <Button
              onClick={() => previewPdf(`/osha-reports/health-check/pdf?year=${year}`)}
              disabled={aiBatchRunning}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="health-check-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Year-End Health Check (AI)
            </Button>
            <Button
              onClick={aiClassifyAll}
              disabled={aiBatchRunning}
              variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="ai-classify-all-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {aiBatchRunning ? 'Analyzing…' : 'AI: Classify All Incidents'}
            </Button>
          </CardContent>
        </Card>

        {/* Company establishment info */}
        <Card className="mb-6" data-testid="osha-company-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" /> Establishment Information
            </CardTitle>
            <CardDescription>Required on OSHA Form 300A. Set once and re-use every year.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'establishmentName', label: 'Establishment name' },
                { key: 'streetAddress', label: 'Street address' },
                { key: 'city', label: 'City' },
                { key: 'state', label: 'State' },
                { key: 'zip', label: 'ZIP' },
                { key: 'naicsCode', label: 'NAICS code' },
                { key: 'sicCode', label: 'SIC code (optional)' },
                { key: 'annualAverageEmployees', label: 'Annual average # employees', type: 'number' },
                { key: 'totalHoursWorked', label: 'Total hours worked last year', type: 'number' },
                { key: 'executiveName', label: 'Certifying executive name' },
                { key: 'executiveTitle', label: 'Executive title' },
                { key: 'executivePhone', label: 'Executive phone' },
              ].map(({ key, label, type }) => (
                <div key={key}>
                  <Label className="text-xs text-gray-500">{label}</Label>
                  <Input
                    type={type || 'text'}
                    value={companyInfo[key] ?? ''}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, [key]: type === 'number' ? Number(e.target.value) || 0 : e.target.value })}
                    data-testid={`company-${key}-input`}
                  />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Button onClick={saveCompany} disabled={savingCompany} className="bg-slate-800 hover:bg-slate-900" data-testid="save-company-btn">
                <Save className="w-4 h-4 mr-2" /> {savingCompany ? 'Saving…' : 'Save Establishment Info'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Incidents classification table */}
        <Card data-testid="osha-incidents-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" /> Classify Incidents
            </CardTitle>
            <CardDescription>
              Toggle each incident as OSHA-recordable and enter days away/restricted so it flows into the 300 log.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading…</div>
            ) : incidents.length === 0 ? (
              <div className="py-8 text-center text-gray-500">No incidents recorded in {year}.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Case #</th>
                      <th className="text-left p-2">Employee</th>
                      <th className="text-left p-2">Location</th>
                      <th className="text-left p-2">Description</th>
                      <th className="text-left p-2 min-w-[180px]">AI Suggestion</th>
                      <th className="text-center p-2">Recordable</th>
                      <th className="text-left p-2">Outcome</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-center p-2">Days Away</th>
                      <th className="text-center p-2">Days Restricted</th>
                      <th className="text-right p-2">301 PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incidents.map((inc) => {
                      const o = inc.osha || {};
                      const isSaving = savingId === inc.id;
                      return (
                        <tr key={inc.id} className="border-b hover:bg-gray-50" data-testid={`osha-row-${inc.id}`}>
                          <td className="p-2 whitespace-nowrap">{(inc.incidentDate || '').slice(0, 10)}</td>
                          <td className="p-2 whitespace-nowrap font-mono text-xs">{o.caseNumber}</td>
                          <td className="p-2">{o.employeeName}</td>
                          <td className="p-2 max-w-[160px] truncate" title={inc.location}>{inc.location}</td>
                          <td className="p-2 max-w-[220px] truncate" title={inc.description}>{inc.description}</td>
                          <td className="p-2 align-top">
                            {o.aiSuggestion ? (
                              <div className="text-xs space-y-1" data-testid={`ai-suggestion-${inc.id}`}>
                                <div className="flex items-center gap-1">
                                  <span
                                    className={
                                      'inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium ' +
                                      (o.aiSuggestion.recordable
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-green-100 text-green-800')
                                    }
                                  >
                                    {o.aiSuggestion.recordable ? <AlertCircle className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                                    {o.aiSuggestion.recordable ? 'Recordable' : 'Not recordable'}
                                  </span>
                                  <span className="text-gray-500">· {o.aiSuggestion.confidence}</span>
                                </div>
                                <div className="text-gray-600 line-clamp-2" title={o.aiSuggestion.reasoning}>
                                  {o.aiSuggestion.reasoning}
                                </div>
                                <div className="flex gap-1 pt-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                                    onClick={() => acceptAiSuggestion(inc)}
                                    data-testid={`accept-ai-${inc.id}`}
                                  >
                                    <Check className="w-3 h-3 mr-1" /> Apply
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 text-xs text-gray-500"
                                    onClick={() => aiClassify(inc.id)}
                                    disabled={aiClassifyingId === inc.id}
                                    data-testid={`re-ai-${inc.id}`}
                                  >
                                    <Wand2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                                onClick={() => aiClassify(inc.id)}
                                disabled={aiClassifyingId === inc.id}
                                data-testid={`ai-classify-${inc.id}`}
                              >
                                <Sparkles className="w-3 h-3 mr-1" />
                                {aiClassifyingId === inc.id ? 'Analyzing…' : 'Ask AI'}
                              </Button>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            <Switch
                              checked={!!o.recordable}
                              onCheckedChange={(v) => patchIncident(inc.id, { recordable: v })}
                              disabled={isSaving}
                              data-testid={`recordable-toggle-${inc.id}`}
                            />
                          </td>
                          <td className="p-2">
                            <Select
                              value={o.outcome || ''}
                              onValueChange={(v) => patchIncident(inc.id, { outcome: v })}
                              disabled={!o.recordable || isSaving}
                            >
                              <SelectTrigger className="w-40 h-8 text-xs">
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                {OUTCOMES.map((oc) => (
                                  <SelectItem key={oc.value} value={oc.value}>{oc.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <Select
                              value={o.injuryOrIllnessType || 'injury'}
                              onValueChange={(v) => patchIncident(inc.id, { injuryOrIllnessType: v })}
                              disabled={!o.recordable || isSaving}
                            >
                              <SelectTrigger className="w-32 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ILLNESS_TYPES.map((t) => (
                                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              className="w-20 h-8 text-xs text-center"
                              value={o.daysAwayFromWork ?? 0}
                              onChange={(e) => patchIncident(inc.id, { daysAwayFromWork: Number(e.target.value) || 0 })}
                              disabled={!o.recordable || isSaving}
                              data-testid={`days-away-${inc.id}`}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              className="w-20 h-8 text-xs text-center"
                              value={o.daysJobTransferRestriction ?? 0}
                              onChange={(e) => patchIncident(inc.id, { daysJobTransferRestriction: Number(e.target.value) || 0 })}
                              disabled={!o.recordable || isSaving}
                              data-testid={`days-restricted-${inc.id}`}
                            />
                          </td>
                          <td className="p-2 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadPdf(`/osha-reports/301/${inc.id}/pdf`, `OSHA_301_${(inc.id||'').slice(0,8)}.pdf`)}
                              data-testid={`download-301-${inc.id}`}
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
